import { z } from "zod"
import { financialActionKey } from "./financial-actions.ts"
import {
  type MobileRedemptionCommandReceipt,
  mobileRedemptionCommandReceiptSchema,
  type OwnedRedemptionLifecycleResponse,
  type OwnedRedemptionQuoteResponse,
  ownedRedemptionLifecycleResponseSchema,
  ownedRedemptionQuoteResponseSchema,
  type PhoneCredentialLinkCompletionTransport,
  type PhoneVerificationChallengeResponse,
  type PhoneVerificationStatus,
  phoneCredentialLinkCompletionTransportSchema,
  phoneVerificationChallengeRequestSchema,
  phoneVerificationChallengeResponseSchema,
  phoneVerificationCompletionSchema,
  phoneVerificationStatusSchema,
  productIdSchema,
  quoteIdSchema,
  redemptionIdSchema,
} from "./todo26-contract.ts"
import { userViewSchema } from "./user-view.ts"

const liveProductSchema = z.strictObject({
  imageUrl: z.url().startsWith("https://"),
  internalProductId: productIdSchema,
  name: z.string().min(1),
  priceWon: z.number().int().positive(),
  supplierCode: z.string().min(1),
})
const liveBalanceSchema = z.strictObject({
  availableWon: z.number().int().nonnegative(),
  expiring30DayWon: z.number().int().nonnegative(),
  expiring90DayWon: z.number().int().nonnegative(),
  heldWon: z.number().int().nonnegative(),
  state: z.enum(["ACTIVE", "EMPTY", "HELD"]),
  totalWon: z.number().int().nonnegative(),
})
const errorResponseSchema = z.looseObject({ code: z.string().min(1).max(100), kind: z.string().max(40).optional(), retryAfterSeconds: z.number().int().min(1).max(86400).optional() })
const csrfResponseHeader = "x-cashhub-csrf-token"
const csrfRequestHeader = "x-cashhub-csrf"

export type Todo26LiveProduct = Readonly<z.infer<typeof liveProductSchema>>
export type Todo26LiveBalance = Readonly<z.infer<typeof liveBalanceSchema>>

type Fetcher = (input: string | URL | Request, init?: RequestInit) => Promise<Response>
type JsonResponse = { readonly body: unknown; readonly headers: Headers; readonly status: number }

export class Todo26LiveHttpError extends Error {
  override readonly name = "Todo26LiveHttpError"
  constructor(
    readonly code: string,
    readonly status: number,
    readonly retryAfterSeconds?: number,
  ) {
    super(code)
  }
}

export class Todo26LiveNetworkError extends Error {
  override readonly name = "Todo26LiveNetworkError"
  constructor() {
    super("TODO26_NETWORK_UNCERTAIN")
  }
}

export interface Todo26LiveClient {
  completePhoneLink(input: PhoneCredentialLinkCompletionTransport): Promise<PhoneVerificationStatus>
  confirmRedemption(quoteId: string): Promise<MobileRedemptionCommandReceipt>
  getBalance(): Promise<Todo26LiveBalance>
  getPhoneStatus(): Promise<PhoneVerificationStatus>
  getProduct(productId: string): Promise<Todo26LiveProduct | null>
  getQuote(quoteId: string): Promise<OwnedRedemptionQuoteResponse>
  getRedemption(redemptionId: string): Promise<OwnedRedemptionLifecycleResponse>
  prepareRedemption(productId: string): Promise<MobileRedemptionCommandReceipt>
  reservePhoneVerificationChallenge(phoneNumber: string): Promise<PhoneVerificationChallengeResponse>
}

class HttpTodo26LiveClient implements Todo26LiveClient {
  private csrfToken: string | null = null

  constructor(
    private readonly apiBase: string,
    private readonly fetcher: Fetcher,
  ) {}

  async completePhoneLink(input: PhoneCredentialLinkCompletionTransport): Promise<PhoneVerificationStatus> {
    const response = phoneVerificationCompletionSchema.parse(await this.post("/web/todo26/identity/phone", phoneCredentialLinkCompletionTransportSchema.parse(input)))
    return phoneVerificationStatusSchema.parse({ checkoutEligibility: response.checkoutEligibility, maskedRecipient: response.maskedRecipient, verificationState: response.verificationState })
  }

  async confirmRedemption(quoteId: string): Promise<MobileRedemptionCommandReceipt> {
    const parsed = quoteIdSchema.parse(quoteId)
    return mobileRedemptionCommandReceiptSchema.parse(await this.post("/web/todo26/redemptions/confirm", { quoteId: parsed }, financialActionKey({ kind: "redemption.confirm", resourceId: parsed })))
  }

  async getBalance(): Promise<Todo26LiveBalance> {
    return liveBalanceSchema.parse((await this.request("/balance", { method: "GET" }, true)).body)
  }

  async getPhoneStatus(): Promise<PhoneVerificationStatus> {
    const response = await this.request("/web/todo26/identity/phone", { method: "GET" }, true)
    const token = response.headers.get(csrfResponseHeader)
    if (token === null) throw new Todo26LiveHttpError("T26_WEB_CSRF_UNAVAILABLE", 502)
    this.csrfToken = token
    return phoneVerificationStatusSchema.parse(response.body)
  }

  async getProduct(productId: string): Promise<Todo26LiveProduct | null> {
    const parsed = productIdSchema.parse(productId)
    try {
      const view = userViewSchema.parse((await this.request(`/products/${parsed}`, { method: "GET" }, true)).body)
      if (view.kind !== "product") throw new Todo26LiveHttpError("T26_WEB_PRODUCT_RESPONSE_INVALID", 502)
      return liveProductSchema.parse({
        imageUrl: view.product.imageUrl,
        internalProductId: view.product.id,
        name: view.product.name,
        priceWon: view.product.priceWon,
        supplierCode: view.product.category,
      })
    } catch (error: unknown) {
      if (error instanceof Todo26LiveHttpError && error.status === 404) return null
      throw error
    }
  }

  async getQuote(quoteId: string): Promise<OwnedRedemptionQuoteResponse> {
    const parsed = quoteIdSchema.parse(quoteId)
    return ownedRedemptionQuoteResponseSchema.parse((await this.request(`/web/todo26/redemptions/quotes/${parsed}`, { method: "GET" }, true)).body)
  }

  async getRedemption(redemptionId: string): Promise<OwnedRedemptionLifecycleResponse> {
    const parsed = redemptionIdSchema.parse(redemptionId)
    return ownedRedemptionLifecycleResponseSchema.parse((await this.request(`/web/todo26/redemptions/${parsed}`, { method: "GET" }, true)).body)
  }

  async prepareRedemption(productId: string): Promise<MobileRedemptionCommandReceipt> {
    const parsed = productIdSchema.parse(productId)
    return mobileRedemptionCommandReceiptSchema.parse(await this.post("/web/todo26/redemptions/prepare", { productId: parsed }, financialActionKey({ kind: "redemption.prepare", resourceId: parsed })))
  }

  async reservePhoneVerificationChallenge(phoneNumber: string): Promise<PhoneVerificationChallengeResponse> {
    const input = phoneVerificationChallengeRequestSchema.parse({ phoneNumber })
    return phoneVerificationChallengeResponseSchema.parse(await this.post("/web/todo26/identity/phone/challenge", input))
  }

  private async post(path: string, body: unknown, idempotencyKey?: string): Promise<unknown> {
    if (this.csrfToken === null) await this.getPhoneStatus()
    const headers = new Headers({ "content-type": "application/json", [csrfRequestHeader]: this.csrfToken ?? "" })
    if (idempotencyKey !== undefined) headers.set("idempotency-key", idempotencyKey)
    return (await this.request(path, { body: JSON.stringify(body), headers, method: "POST" }, false)).body
  }

  private async request(path: string, init: RequestInit, refreshGet: boolean): Promise<JsonResponse> {
    try {
      let response = await this.fetcher(`${this.apiBase}${path}`, { ...init, credentials: "include", signal: AbortSignal.timeout(10_000) })
      if (response.status === 401 && refreshGet && init.method === "GET") {
        const refreshed = await this.fetcher(`${this.apiBase}/identity/refresh`, { body: JSON.stringify({ client: "WEB" }), credentials: "include", headers: { "content-type": "application/json" }, method: "POST", signal: AbortSignal.timeout(10_000) })
        if (refreshed.ok) response = await this.fetcher(`${this.apiBase}${path}`, { ...init, credentials: "include", signal: AbortSignal.timeout(10_000) })
      }
      const body: unknown = await response.json().catch(() => ({ code: "T26_WEB_INVALID_RESPONSE" }))
      if (!response.ok) {
        const parsed = errorResponseSchema.safeParse(body)
        throw new Todo26LiveHttpError(parsed.success ? parsed.data.code : "T26_WEB_INVALID_RESPONSE", response.status, parsed.success ? parsed.data.retryAfterSeconds : undefined)
      }
      return { body, headers: response.headers, status: response.status }
    } catch (error: unknown) {
      if (error instanceof Todo26LiveHttpError || error instanceof z.ZodError) throw error
      throw new Todo26LiveNetworkError()
    }
  }
}

export const createTodo26LiveClient = (input: { readonly apiBase?: string; readonly fetcher?: Fetcher } = {}): Todo26LiveClient => new HttpTodo26LiveClient(input.apiBase ?? process.env["NEXT_PUBLIC_API_BASE_URL"] ?? "/api", input.fetcher ?? globalThis.fetch.bind(globalThis))

export const liveProductAffordability = (product: Todo26LiveProduct, balance: Todo26LiveBalance): "AFFORDABLE" | "INSUFFICIENT_POINTS" => (product.priceWon <= balance.availableWon ? "AFFORDABLE" : "INSUFFICIENT_POINTS")
